import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const empty = { name: "", contact_person: "", phone: "", email: "", address: "", gstin: "", payment_terms: "", opening_balance: 0, status: "Active" };

export default function SupplierMaster() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [outstanding, setOutstanding] = useState({});
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/vendors").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
    api.get("/reports/vendor-outstanding").then(r => setOutstanding(Object.fromEntries(r.data.map(x => [x.id, x.outstanding])))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...f,
        opening_balance: Number(f.opening_balance) || 0,
        contact_person: f.contact_person || null, email: f.email || null,
        address: f.address || null, gstin: f.gstin || null, payment_terms: f.payment_terms || null,
      };
      if (editId) await api.patch(`/vendors/${editId}`, payload);
      else await api.post("/vendors", payload);
      toast.success(editId ? "Supplier updated" : "Supplier added");
      setF(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (v) => {
    setF({ ...empty, ...Object.fromEntries(Object.keys(empty).map(k => [k, v[k] ?? empty[k]])) });
    setEditId(v.id); setOpen(true);
  };

  const del = async (id) => {
    if (!confirm("Delete this supplier?")) return;
    try { await api.delete(`/vendors/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Truck className="w-3 h-3"/> Accounts & Finance · Creditors — balances reflect in Vendor Ledger</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Supplier Master</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(empty); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-supplier-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
              <Plus className="w-4 h-4 mr-1"/> New Supplier
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Supplier</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3" onKeyDownCapture={handleSimpleFormNav}>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Supplier Name *</Label>
                <Input data-testid="sup-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Contact Person</Label>
                <Input data-testid="sup-contact" value={f.contact_person} onChange={(e) => setF(x => ({ ...x, contact_person: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone Number *</Label>
                <Input data-testid="sup-phone" value={f.phone} onChange={(e) => setF(x => ({ ...x, phone: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                <Input data-testid="sup-email" value={f.email} onChange={(e) => setF(x => ({ ...x, email: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">GST Number</Label>
                <Input data-testid="sup-gst" value={f.gstin} onChange={(e) => setF(x => ({ ...x, gstin: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Payment Terms</Label>
                <Input data-testid="sup-terms" value={f.payment_terms} onChange={(e) => setF(x => ({ ...x, payment_terms: e.target.value }))} placeholder="e.g. Net 30" className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Opening Balance (₹ owed)</Label>
                <Input data-testid="sup-opening" type="number" value={f.opening_balance} onChange={(e) => setF(x => ({ ...x, opening_balance: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
                <Select value={f.status} onValueChange={(v) => setF(x => ({ ...x, status: v }))}>
                  <SelectTrigger data-testid="sup-status" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
                <Textarea data-testid="sup-address" value={f.address} onChange={(e) => setF(x => ({ ...x, address: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
              </div>
            </div>
            <Button data-testid="save-supplier-btn" onClick={save} disabled={!f.name || !f.phone} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] mt-3">
              {editId ? "Update Supplier" : "Save Supplier"}
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Supplier Name</th>
              <th className="text-left p-3">Phone Number</th>
              <th className="text-left p-3">GST Number</th>
              <th className="text-right p-3">Outstanding Amount</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No suppliers yet</td></tr>}
            {pg.pageItems.map(v => {
              const active = (v.status || "Active") === "Active";
              return (
                <tr key={v.id} data-testid={`supplier-row-${v.id}`} className="border-t border-border hover:bg-secondary/50">
                  <td className="p-3 font-medium">{v.name}{v.contact_person && <span className="text-xs text-muted-foreground ml-2">({v.contact_person})</span>}</td>
                  <td className="p-3 font-mono text-xs">{v.phone}</td>
                  <td className="p-3 font-mono text-xs uppercase">{v.gstin || "-"}</td>
                  <td className={`p-3 text-right font-mono ${outstanding[v.id] > 0 ? "text-destructive" : ""}`}>{inr(outstanding[v.id] || 0)}</td>
                  <td className="p-3"><span className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${active ? "border-green-500/40 bg-green-500/10 text-green-500" : "border-destructive/40 bg-destructive/10 text-destructive"}`}>{v.status || "Active"}</span></td>
                  <td className="p-3 text-right">
                    <Button data-testid={`edit-supplier-${v.id}`} size="sm" variant="ghost" onClick={() => edit(v)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                    <Button data-testid={`del-supplier-${v.id}`} size="sm" variant="ghost" onClick={() => del(v.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
